import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api, createEmptyDraft, newWorkingReference, uploadQuoteDocument, deleteQuote } from "@/services/api";
import { VARIANCE_PCT, GP_PCT } from "@/components/BackendPriceStrip";
import { CustomerPicker } from "@/components/CustomerPicker";
import type { QuoteCustomer } from "@/types";
import { isRuleExpired, BrandRuleList } from "@/components/BrandLockTable";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows, overlayMaxWidth } from "@/styles/tokens";

const WAREHOUSES = ["Bangalore Warehouse", "Chennai Warehouse"];

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};
const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <span style={LABEL}>
      {children}
      {required && <span style={{ color: semanticColors.textPlaceholder, textTransform: "none" }}> *</span>}
    </span>
  );
}

function TextField({
  label, testid, value, onChange, placeholder, required,
}: { label: string; testid: string; value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <input data-testid={testid} type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} style={INPUT} />
    </div>
  );
}

function SelectField({
  label, testid, value, options, onChange, placeholder, required,
}: { label: string; testid: string; value: string; options: readonly string[]; onChange: (v: string) => void; placeholder?: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} style={INPUT}>
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export function NewQuoteModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  // The picked customer. endClient is DERIVED from it below rather than
  // typed: brand-lock and discount rules are keyed by client name, and two
  // fields for one fact is how they drift apart.
  const [customer, setCustomer] = useState<QuoteCustomer | undefined>(undefined);
  const endClient = customer?.entity?.name ?? "";
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [warehouse, setWarehouse] = useState(WAREHOUSES[0]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const allBrandLockRules = useMemo(() => api.getBrandLockRules(), []);
  const clientRules = useMemo(
    () => endClient ? allBrandLockRules.filter((r) => r.endClient === endClient && !isRuleExpired(r)) : [],
    [allBrandLockRules, endClient]
  );

  // A customer is now REQUIRED and must be a real record -- an id, not a
  // string someone typed.
  const canCreate = !!customer?.entity?.id && projectId.trim() !== "";

  // ---- START FROM PO ----------------------------------------------------
  //
  // ⚠️ NO CUSTOMER AND NO PROJECT ID ARE REQUIRED HERE, WHICH IS THE WHOLE
  // POINT. The customer is printed on the PO and is not known until it has
  // been read, and the owner's rule is explicit: never invent a placeholder
  // customer. A draft with no customer has been allowed since 17.0.1.34.0;
  // only FINALISING needs one.
  //
  // ⚠️ AND NO FILE IS EVER HELD UNSAVED IN THE BROWSER. The order is create
  // the row, then attach the PDF to it -- so the bytes exist in exactly one
  // place from the moment they are chosen. The alternative (hold the file,
  // extract, then save if the user likes the result) is what this ordering
  // exists to avoid: a browser tab holding the only copy of a customer's
  // paperwork.
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [poBusy, setPoBusy] = useState("");
  const [poError, setPoError] = useState("");

  const startFromPo = async (file: File) => {
    setPoError("");

    // PDF ONLY, and checked on the file's declared type AND its extension.
    // The document route itself accepts PNG and JPEG too, but this flow feeds
    // the EXTRACTOR, which reads a text layer -- so an image would create a
    // draft that can never be read from, which is a dead end dressed up as a
    // start.
    const isPdf = file.type === "application/pdf"
      || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setPoError(
        "Start from PO needs a PDF. An image cannot be read for its lines -- "
        + "attach it to a quote instead, or re-export the PO as a PDF."
      );
      return;
    }

    setPoBusy("Creating the draft...");
    const draft = await createEmptyDraft(newWorkingReference(), VARIANCE_PCT, GP_PCT);
    if (!draft.ok || draft.quoteId === undefined) {
      setPoBusy("");
      setPoError(draft.error || "The draft could not be created.");
      return;
    }

    setPoBusy("Attaching the PO...");
    const upload = await uploadQuoteDocument(draft.quoteId, file);
    if (!upload.ok) {
      // ⚠️ A HALF-DONE START IS UNDONE, NOT REPORTED AS A START. The draft
      // exists and is empty, which is exactly what delete_quote accepts, so
      // the flow cleans up after itself rather than leaving a blank draft
      // nobody asked for in the drafts list.
      const cleanup = await deleteQuote(draft.quoteId);
      setPoBusy("");
      setPoError(
        cleanup.ok
          ? `The PO was not attached: ${upload.error} Nothing was saved.`
          // ⚠️ AND IF THE CLEAN-UP ALSO FAILS, SAY SO AND NAME THE DRAFT.
          // Claiming "nothing was saved" would be false, and the person is
          // the only one who can now decide what to do with it.
          : `The PO was not attached: ${upload.error} An empty draft `
            + `(#${draft.quoteId}) was left behind and could not be removed `
            + `automatically: ${cleanup.error}`
      );
      return;
    }

    setPoBusy("");
    onClose();
    // The REAL quote id, not the working reference: this screen reads the
    // draft back out of Odoo, so the PO panel is there on arrival and a
    // reload lands on the same row rather than depending on any tab state.
    navigate(`/saved-quotes/${draft.quoteId}`);
  };

  const handleCreate = () => {
    if (!canCreate) return;
    const quote = api.createQuote({
      endClient, customer, projectId: projectId.trim(), projectName: projectName.trim(),
      warehouse,
    });
    onClose();
    navigate(`/quotes/${quote.quoteNo}`);
  };

  return (
    <div
      data-testid="new-quote-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, backgroundColor: semanticColors.overlay,
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        data-testid="new-quote-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: layout.modalWidth,
          // Inert at any width >= 496px -- see overlayMaxWidth.
          maxWidth: overlayMaxWidth,
          maxHeight: "90vh",
          backgroundColor: semanticColors.surface,
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          boxShadow: shadows.modal,
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ padding: `${spacing[16]}px ${spacing[20]}px`, borderBottom: `1px solid ${semanticColors.border}` }}
        >
          <div
            data-testid="new-quote-title"
            style={{
              fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
              letterSpacing: typography.letterSpacing.tight,
            }}
          >
            New Quote
          </div>
          <button
            data-testid="new-quote-close"
            onClick={onClose}
            title="Close"
            style={{
              width: heights.iconButton, height: heights.iconButton, padding: 0, cursor: "pointer",
              background: "transparent", border: "none", color: semanticColors.textSecondary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={iconSize.lg} strokeWidth={iconStroke.regular} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: spacing[20] }}>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>
            {/* START FROM A CUSTOMER PO.
                Placed FIRST because it is the branch that skips everything
                below it: a PO names the customer, the project and the lines,
                so filling this form in first and then reading the paperwork
                would be asking for the same facts twice. */}
            <div
              data-testid="nq-start-from-po"
              style={{
                padding: spacing[12], backgroundColor: semanticColors.panelBg,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                display: "flex", flexDirection: "column", gap: spacing[8],
              }}
            >
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: spacing[12],
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
                  <div style={{
                    fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
                    textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                    fontWeight: typography.fontWeight.medium,
                  }}>
                    Have the customer&rsquo;s PO?
                  </div>
                  <div style={{
                    fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
                  }}>
                    Start from the PDF and the lines are read for you. You confirm
                    every line before anything is added, and prices always come from
                    our own pricing, never from the PO.
                  </div>
                </div>
                <button
                  data-testid="nq-start-from-po-button"
                  onClick={() => fileInput.current?.click()}
                  disabled={poBusy !== ""}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`, flexShrink: 0,
                    backgroundColor: semanticColors.surface,
                    color: poBusy ? semanticColors.textDisabled : semanticColors.textPrimary,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    fontSize: typography.fontSize.base,
                    fontWeight: typography.fontWeight.medium,
                    cursor: poBusy ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {poBusy || "Start from PO"}
                </button>
              </div>

              {/* ⚠️ accept IS A HINT, NOT THE CHECK. A file chooser's filter
                  can be overridden in every browser, so startFromPo tests the
                  file itself and the server tests the bytes. */}
              <input
                data-testid="nq-po-file"
                ref={fileInput}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // Cleared so choosing the SAME file again still fires a
                  // change event, which is how a retry after a failure works.
                  e.target.value = "";
                  if (file) void startFromPo(file);
                }}
              />

              {poError !== "" && (
                <div
                  data-testid="nq-po-error"
                  style={{
                    padding: spacing[8], borderRadius: radius.base,
                    backgroundColor: semanticColors.destructiveBg,
                    border: `1px solid ${semanticColors.destructiveBorder}`,
                    color: semanticColors.destructive, fontSize: typography.fontSize.sm,
                  }}
                >
                  {poError}
                </div>
              )}
            </div>

            {/* The picker, where a free-text end-client dropdown used to be.
                A quote is now addressed to a real customer RECORD -- see
                CustomerPicker for why that mattered. */}
            <CustomerPicker value={customer} onChange={setCustomer} />

            {endClient !== "" && (
              <div
                data-testid="new-quote-brand-preview"
                style={{
                  padding: spacing[12], backgroundColor: semanticColors.panelBg,
                  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                  display: "flex", flexDirection: "column", gap: spacing[8],
                }}
              >
                <div style={{
                  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
                  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                  fontWeight: typography.fontWeight.medium,
                }}>
                  Brand rules for this client
                </div>
                <BrandRuleList rules={clientRules} testIdPrefix="new-quote-brand" />
              </div>
            )}

            <TextField
              label="Project ID" testid="nq-project-id" value={projectId}
              onChange={setProjectId} placeholder="e.g. PRJ-2026-0147" required
            />

            <TextField
              label="Project Name" testid="nq-project-name" value={projectName}
              onChange={setProjectName} placeholder="e.g. Bangalore Campus Floor 3 Cabling"
            />

            <SelectField
              label="Destination Warehouse" testid="nq-warehouse" value={warehouse}
              options={WAREHOUSES} onChange={setWarehouse}
            />

            {/* No "Quote Validity" picker (remove-new-quote-validity-picker,
                2026-09-23): validity comes only from Settings > Company and is
                stored on the quote when it is finalised. */}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid ${semanticColors.border}`,
            padding: spacing[16],
            display: "flex", gap: spacing[8], justifyContent: "flex-end",
          }}
        >
          <button
            data-testid="new-quote-cancel"
            className="btn-secondary"
            onClick={onClose}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
              color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base, fontSize: typography.fontSize.base, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            data-testid="new-quote-create"
            className="btn-primary"
            onClick={handleCreate}
            disabled={!canCreate}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              backgroundColor: canCreate ? colors.indigo600 : semanticColors.textDisabled,
              color: semanticColors.onAccent, border: "none", borderRadius: radius.base,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
              cursor: canCreate ? "pointer" : "not-allowed",
            }}
          >
            Create Quote
          </button>
        </div>
      </div>
    </div>
  );
}
